import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { API_BASE_URL } from '@/lib/config'
import { isTokenFresh, refreshJWTToken, redirectToLogin } from '@/lib/auth'
import { useAuthStore } from '@/stores/useAuthStore'
import type { Participant } from '@/stores/useCallStore'
import { tokenNeedsVerification } from '@/lib/verificationConnexion'
import type { MessageRelais } from '@/lib/relaisOnglet'

// ============================================================
// Types des events SSE
// ============================================================

export interface SMSEvent {
  id: number
  from_number: string
  to_number: string
  message: string
  direction: 'inbound' | 'outbound'
  created_at: string
  /** An already announced text that changed (a late image): reload, no new-text toast. */
  mise_a_jour?: boolean
}

export interface VoicemailEvent {
  id: number
  from_number: string
  recording_url?: string
  recording_duration?: number
  is_read?: boolean
  is_listened?: boolean
  status?: string
  s3_available?: boolean
  transcription?: string | null
  created_at: string
}

export interface CallEvent {
  id: number
  call_sid?: string
  from_number: string
  to_number: string
  direction: 'inbound' | 'outbound'
  status: string
  has_voicemail?: boolean
  duration?: number
  started_at?: string
}

export interface BatchCallEvent {
  type: 'batch_call_started' | 'batch_call_updated' | 'batch_call_completed'
  data: Record<string, unknown>
}

export interface HoldClientHangupEvent {
  call_sid?: string
  reason?: string
}

/** Collaboration d'équipe (plan Team). Le canal est celui de l'entreprise :
 * un événement qui vise une personne porte son `to_user_id`, l'écran filtre. */
export interface NoteInterneEvent {
  id: number
  phone: string
  line: string | null
  by_user_id: number
}

export interface MentionEvent {
  to_user_id: number
  by_user_id: number
  by_name: string
  phone: string
  line: string | null
  note_id: number
  text: string
}

export interface FilEquipeEvent {
  action: 'assign' | 'done' | 'reopen'
  phone: string
  line: string | null
  assigned_to_user_id: number | null
  by_user_id: number
  by_name: string
}

export interface FrappeEvent {
  phone: string
  line: string | null
  user_id: number
  name: string
  mode: 'sms' | 'note'
}

/** Messages entre collègues. Sans le texte : le canal est celui de toute
 * l'entreprise, l'écran concerné recharge le fil par l'API. */
export interface MessageCollegueEvent {
  id: number
  to_user_id: number
  by_user_id: number
  by_name: string
}

export interface MessageCollegueLuEvent {
  user_id: number
  collegue_user_id: number
}

/** Do not disturb changed for me, on this screen or another one (telephony/services/ne_pas_deranger.py). */
export interface NePasDerangerEvent {
  dnd_enabled: boolean
  status: 'ONLINE' | 'OFFLINE'
}

export interface FrappeCollegueEvent {
  to_user_id: number
  user_id: number
  name: string
}

/** The support asks to see the screen, or the request is withdrawn (screen sharing). */
/** Step of an announced transfer (telephony/transfert_annonce.py). */
export interface TransfertAnnonceEvent {
  conference: string
  evenement:
    | 'appel_collegue'
    | 'collegue_en_ligne'
    | 'agent_revenu'
    | 'collegue_absent'
    | 'collegue_parti'
    | 'collegue_injoignable'
    | 'echec'
    | 'annule'
    | 'passe'
    | 'client_parti'
    | 'fin'
    // Call with several people
    | 'invite_appele'
    | 'invite_en_ligne'
    | 'invite_absent'
    | 'invite_parti'
    | 'invite_injoignable'
    | 'reuni'
    | 'relais'
  user_id: number | null
  label: string
  /** The people added to the call after this step (neither caller nor agent). */
  participants?: Participant[]
}

export interface ScreenShareEvent {
  type: 'demande' | 'annulee'
  id: number
  support?: string
  expire_dans?: number
}

interface SSECallbacks {
  sms: Set<(sms: SMSEvent) => void>
  voicemail: Set<(voicemail: VoicemailEvent) => void>
  voicemail_transcribed: Set<(voicemail: VoicemailEvent) => void>
  call: Set<(call: CallEvent) => void>
  batch_call: Set<(event: BatchCallEvent) => void>
  hold_client_hangup: Set<(event: HoldClientHangupEvent) => void>
  contact_suggestions: Set<(event: ContactSuggestionsEvent) => void>
  note_interne: Set<(event: NoteInterneEvent) => void>
  mention: Set<(event: MentionEvent) => void>
  fil_equipe: Set<(event: FilEquipeEvent) => void>
  frappe: Set<(event: FrappeEvent) => void>
  message_collegue: Set<(event: MessageCollegueEvent) => void>
  message_collegue_lu: Set<(event: MessageCollegueLuEvent) => void>
  frappe_collegue: Set<(event: FrappeCollegueEvent) => void>
  partage_ecran: Set<(event: ScreenShareEvent) => void>
  transfert_annonce: Set<(event: TransfertAnnonceEvent) => void>
  ne_pas_deranger: Set<(event: NePasDerangerEvent) => void>
  relais_telephone: Set<(event: MessageRelais) => void>
}

/** Des valeurs de fiche entendues dans un appel attendent une approbation. */
export interface ContactSuggestionsEvent {
  phone_number: string
  count: number
}

interface SSEContextValue {
  connected: boolean
  connect: () => void
  disconnect: () => void
  onSMS: (cb: (sms: SMSEvent) => void) => () => void
  onVoicemail: (cb: (voicemail: VoicemailEvent) => void) => () => void
  onVoicemailTranscribed: (cb: (voicemail: VoicemailEvent) => void) => () => void
  onCallUpdate: (cb: (call: CallEvent) => void) => () => void
  onBatchCallEvent: (cb: (event: BatchCallEvent) => void) => () => void
  onHoldClientHangup: (cb: (event: HoldClientHangupEvent) => void) => () => void
  onContactSuggestions: (cb: (event: ContactSuggestionsEvent) => void) => () => void
  onNoteInterne: (cb: (event: NoteInterneEvent) => void) => () => void
  onMention: (cb: (event: MentionEvent) => void) => () => void
  onFilEquipe: (cb: (event: FilEquipeEvent) => void) => () => void
  onFrappe: (cb: (event: FrappeEvent) => void) => () => void
  onMessageCollegue: (cb: (event: MessageCollegueEvent) => void) => () => void
  onMessageCollegueLu: (cb: (event: MessageCollegueLuEvent) => void) => () => void
  onFrappeCollegue: (cb: (event: FrappeCollegueEvent) => void) => () => void
  onScreenShare: (cb: (event: ScreenShareEvent) => void) => () => void
  onTransfertAnnonce: (cb: (event: TransfertAnnonceEvent) => void) => () => void
  onNePasDeranger: (cb: (event: NePasDerangerEvent) => void) => () => void
  /** One account, one phone: claims from the person's phones the BroadcastChannel cannot reach. */
  onRelaisTelephone: (cb: (event: MessageRelais) => void) => () => void
}

const SSEContext = createContext<SSEContextValue | undefined>(undefined)

// ============================================================
// Routes où le SSE NE doit PAS se connecter (pas authentifié / pas de company)
// ============================================================
const SSE_DISABLED_PATHS = [
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/auth/callback',
  '/onboarding',
  '/subscribe',
  '/checkout',
  '/verification',
]

export function shouldSkipSSE(pathname: string): boolean {
  return SSE_DISABLED_PATHS.some((path) => pathname.includes(path))
}

// ============================================================
// Provider
// ============================================================

export function SSEProvider({ children }: { children: ReactNode }) {
  const [connected, setConnected] = useState(false)
  const eventSourceRef = useRef<EventSource | null>(null)
  const reconnectDelayRef = useRef<number>(1000)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastEventIdRef = useRef<string | null>(null)
  const mountedRef = useRef(true)
  // Circuit-breaker : on stoppe les retries après N échecs successifs SANS
  // onopen entre-deux. Évite qu'un tab oublié spam le backend pendant des
  // heures avec un token définitivement invalide (cf incident 2026-05-21).
  // IMPORTANT : on ne force pas de logout (anti-pattern desktop, cf Auth0).
  // Un échec SSE ≠ token invalide. La perte réseau ne doit jamais déconnecter
  // l'user. On reprend la connexion sur évènement `online` ou nouvelle action.
  const failedAttemptsRef = useRef<number>(0)
  const pausedRef = useRef<boolean>(false)
  const MAX_FAILED_ATTEMPTS = 10

  const callbacksRef = useRef<SSECallbacks>({
    sms: new Set(),
    voicemail: new Set(),
    voicemail_transcribed: new Set(),
    call: new Set(),
    batch_call: new Set(),
    hold_client_hangup: new Set(),
    contact_suggestions: new Set(),
    note_interne: new Set(),
    mention: new Set(),
    fil_equipe: new Set(),
    frappe: new Set(),
    message_collegue: new Set(),
    message_collegue_lu: new Set(),
    frappe_collegue: new Set(),
    partage_ecran: new Set(),
    transfert_annonce: new Set(),
    ne_pas_deranger: new Set(),
    relais_telephone: new Set(),
  })

  const scheduleReconnect = useCallback(() => {
    if (!mountedRef.current) return
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)

    reconnectTimerRef.current = setTimeout(() => {
      if (mountedRef.current) connect()
    }, reconnectDelayRef.current)

    reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, 30_000)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const connect = useCallback(() => {
    if (!mountedRef.current) return

    // Si le circuit-breaker a mis le SSE en pause (échecs réseau prolongés),
    // ne pas spammer le backend. On reprendra via `online` event ou action user.
    if (pausedRef.current) return

    if (
      eventSourceRef.current &&
      eventSourceRef.current.readyState !== EventSource.CLOSED
    ) {
      return
    }

    const token = useAuthStore.getState().accessToken
    if (!token) return
    // Sign-in code not confirmed yet: the server refuses the stream.
    if (tokenNeedsVerification(token)) return

    if (!isTokenFresh(60_000)) {
      void refreshJWTToken().then((resultat) => {
        if (!mountedRef.current) return
        if (resultat === 'ok') connect()
        else if (resultat === 'expire') redirectToLogin('expired')
        else scheduleReconnect()
      })
      return
    }

    let url = `${API_BASE_URL}/api/events/stream/?token=${encodeURIComponent(token)}`
    if (lastEventIdRef.current) {
      url += `&lastEventId=${encodeURIComponent(lastEventIdRef.current)}`
    }

    try {
      const es = new EventSource(url)
      eventSourceRef.current = es

      es.onopen = () => {
        if (!mountedRef.current) return
        setConnected(true)
        reconnectDelayRef.current = 1000
        failedAttemptsRef.current = 0
        pausedRef.current = false
        console.info('[SSE] connected to /api/events/stream/')
      }

      const handle = <T,>(eventName: string, target: keyof SSECallbacks) => {
        es.addEventListener(eventName, (event) => {
          if (!mountedRef.current) return
          const messageEvent = event as MessageEvent
          try {
            const data = JSON.parse(messageEvent.data) as T
            lastEventIdRef.current = messageEvent.lastEventId || null
            callbacksRef.current[target].forEach((cb) =>
              (cb as (data: T) => void)(data),
            )
          } catch (err) {
            console.error(`[SSE] Error parsing ${eventName} event:`, err)
          }
        })
      }

      handle<SMSEvent>('sms', 'sms')
      handle<VoicemailEvent>('voicemail', 'voicemail')
      handle<VoicemailEvent>('voicemail_transcribed', 'voicemail_transcribed')
      handle<CallEvent>('call', 'call')
      handle<HoldClientHangupEvent>('hold_client_hangup', 'hold_client_hangup')
      handle<ContactSuggestionsEvent>('contact_suggestions', 'contact_suggestions')
      handle<NoteInterneEvent>('note_interne', 'note_interne')
      handle<MentionEvent>('mention', 'mention')
      handle<FilEquipeEvent>('fil_equipe', 'fil_equipe')
      handle<FrappeEvent>('frappe', 'frappe')
      handle<MessageCollegueEvent>('message_collegue', 'message_collegue')
      handle<MessageCollegueLuEvent>('message_collegue_lu', 'message_collegue_lu')
      handle<FrappeCollegueEvent>('frappe_collegue', 'frappe_collegue')
      handle<ScreenShareEvent>('partage_ecran', 'partage_ecran')
      handle<TransfertAnnonceEvent>('transfert_annonce', 'transfert_annonce')
      handle<NePasDerangerEvent>('ne_pas_deranger', 'ne_pas_deranger')
      handle<MessageRelais>('relais_telephone', 'relais_telephone')

      // Batch call events : on wrap dans { type, data }
      const handleBatch = (eventName: BatchCallEvent['type']) => {
        es.addEventListener(eventName, (event) => {
          if (!mountedRef.current) return
          const messageEvent = event as MessageEvent
          try {
            const data = JSON.parse(messageEvent.data)
            lastEventIdRef.current = messageEvent.lastEventId || null
            callbacksRef.current.batch_call.forEach((cb) =>
              cb({ type: eventName, data }),
            )
          } catch (err) {
            console.error(`[SSE] Error parsing ${eventName} event:`, err)
          }
        })
      }
      handleBatch('batch_call_started')
      handleBatch('batch_call_updated')
      handleBatch('batch_call_completed')

      es.onerror = () => {
        if (!mountedRef.current) return
        setConnected(false)
        es.close()
        eventSourceRef.current = null

        // Circuit-breaker : après N échecs successifs sans onopen, on met
        // le SSE en pause pour économiser CPU et arrêter de spam le backend.
        // On NE redirige PAS vers login : un échec SSE ≠ token invalide
        // (perte réseau prolongée, backend down, etc.). La session reste
        // valide pour les API REST. Reprise via évènement `online` ou
        // action user (focus, navigation).
        failedAttemptsRef.current += 1
        if (failedAttemptsRef.current >= MAX_FAILED_ATTEMPTS) {
          console.warn(
            `[SSE] ${MAX_FAILED_ATTEMPTS} échecs consécutifs → pause (sans logout)`,
          )
          if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current)
            reconnectTimerRef.current = null
          }
          pausedRef.current = true
          return
        }

        console.warn(`[SSE] connection lost (échec #${failedAttemptsRef.current})`)

        if (!isTokenFresh(60_000)) {
          void refreshJWTToken().then((resultat) => {
            if (!mountedRef.current) return
            if (resultat === 'ok') {
              reconnectDelayRef.current = 1000
              connect()
            } else if (resultat === 'expire') {
              redirectToLogin('expired')
            } else {
              scheduleReconnect()
            }
          })
        } else {
          scheduleReconnect()
        }
      }
    } catch (err) {
      console.error('[SSE] Failed to create EventSource:', err)
      scheduleReconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleReconnect])

  const disconnect = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    setConnected(false)
  }, [])

  useEffect(() => {
    // CRITIQUE : remettre mountedRef à true à chaque mount.
    // Sinon en StrictMode (mount → cleanup → re-mount), mountedRef reste false
    // et tous les connect() retournent early.
    mountedRef.current = true

    const handleLoginComplete = () => {
      if (shouldSkipSSE(window.location.pathname)) return
      connect()
    }

    const handleTokenRefreshed = () => {
      if (!mountedRef.current) return
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
      reconnectDelayRef.current = 1000
      connect()
    }

    // Retour du réseau : on lève la pause éventuelle et on reconnecte.
    // Couvre le cas typique desktop : laptop en veille / Wi-Fi instable.
    const handleOnline = () => {
      if (!mountedRef.current) return
      if (!pausedRef.current && eventSourceRef.current) return
      console.info('[SSE] network back online, resuming')
      pausedRef.current = false
      failedAttemptsRef.current = 0
      reconnectDelayRef.current = 1000
      connect()
    }

    window.addEventListener('login-complete', handleLoginComplete)
    window.addEventListener('token-refreshed', handleTokenRefreshed)
    window.addEventListener('online', handleOnline)

    // Auto-connect au montage si déjà authentifié et pas sur une route bloquée
    const token = useAuthStore.getState().accessToken
    if (token && !shouldSkipSSE(window.location.pathname)) {
      connect()
    }

    return () => {
      mountedRef.current = false
      window.removeEventListener('login-complete', handleLoginComplete)
      window.removeEventListener('token-refreshed', handleTokenRefreshed)
      window.removeEventListener('online', handleOnline)
      disconnect()
    }
  }, [connect, disconnect])

  // ============================================================
  // Subscribe API
  // ============================================================

  const subscribe = useCallback(
    <K extends keyof SSECallbacks>(
      eventType: K,
      callback: (data: never) => void,
    ): (() => void) => {
      const set = callbacksRef.current[eventType] as Set<(data: never) => void>
      set.add(callback)
      return () => set.delete(callback)
    },
    [],
  )

  const value: SSEContextValue = {
    connected,
    connect,
    disconnect,
    onSMS: (cb) => subscribe('sms', cb as never),
    onVoicemail: (cb) => subscribe('voicemail', cb as never),
    onVoicemailTranscribed: (cb) => subscribe('voicemail_transcribed', cb as never),
    onCallUpdate: (cb) => subscribe('call', cb as never),
    onBatchCallEvent: (cb) => subscribe('batch_call', cb as never),
    onHoldClientHangup: (cb) => subscribe('hold_client_hangup', cb as never),
    onContactSuggestions: (cb) => subscribe('contact_suggestions', cb as never),
    onNoteInterne: (cb) => subscribe('note_interne', cb as never),
    onMention: (cb) => subscribe('mention', cb as never),
    onFilEquipe: (cb) => subscribe('fil_equipe', cb as never),
    onFrappe: (cb) => subscribe('frappe', cb as never),
    onMessageCollegue: (cb) => subscribe('message_collegue', cb as never),
    onMessageCollegueLu: (cb) => subscribe('message_collegue_lu', cb as never),
    onFrappeCollegue: (cb) => subscribe('frappe_collegue', cb as never),
    onScreenShare: (cb) => subscribe('partage_ecran', cb as never),
    onTransfertAnnonce: (cb) => subscribe('transfert_annonce', cb as never),
    onNePasDeranger: (cb) => subscribe('ne_pas_deranger', cb as never),
    onRelaisTelephone: (cb) => subscribe('relais_telephone', cb as never),
  }

  return <SSEContext.Provider value={value}>{children}</SSEContext.Provider>
}

export function useSSE(): SSEContextValue {
  const ctx = useContext(SSEContext)
  if (!ctx) throw new Error('useSSE must be used within an SSEProvider')
  return ctx
}
